import classNames from 'classnames';
import * as React from 'react';
import type { ReactNode } from 'react';
import type { StrictOmit } from '../Utils/typeUtils';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { Theme } from '~/Utils/Theme';
import styles from './Button.module.css';
import type { ButtonSize } from './ButtonSize.csharp';
import type { ButtonTier } from './ButtonTier.csharp';
import { ButtonType } from './ButtonType.csharp';

type HtmlButtonType = 'submit' | 'reset' | 'button';

export interface DefaultButtonProps {
    children: ReactNode;
    className?: string;
    htmltype?: HtmlButtonType;
    /* STYLES PROPS */
    size?: ButtonSize;
    tier?: ButtonTier;
    type?: ButtonType;
    theme?: Theme;
}

type NativeButtonAttributes = Pick<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    | 'aria-selected'
    | 'aria-controls'
    | 'aria-expanded'
    | 'role'
    | 'aria-haspopup'
    | 'aria-describedby'
    | 'aria-label'
    | 'tabIndex'
    | 'title'
    | 'id'
    | 'disabled'
    | 'draggable'
    | 'onClick'
    | 'onFocus'
    | 'onMouseDown'
>;

export type ButtonProps = DefaultButtonProps & NativeButtonAttributes;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const { children, theme, ...passThroughProps } = props;
    const { type } = passThroughProps;

    if (type && type !== ButtonType.Clear) {
        return (
            <ThemedButton {...passThroughProps} theme={theme} ref={ref}>
                {children}
            </ThemedButton>
        );
    }

    return (
        <RawButton {...passThroughProps} ref={ref}>
            {children}
        </RawButton>
    );
});

type RawButtonProps = StrictOmit<ButtonProps, 'theme'>;

const RawButton = React.forwardRef<HTMLButtonElement, RawButtonProps>((props, ref) => {
    const { className, children, size, tier, type, htmltype = 'button', ...otherProps } = props;

    const getButtonClassName = () =>
        classNames(
            type && styles[`Variant--${type}`],
            tier && styles[`Variant--${tier}`],
            size && styles[`Variant--${size}`],
            className
        );

    return (
        <button {...otherProps} type={htmltype} ref={ref} className={getButtonClassName()}>
            {children}
        </button>
    );
});

const ThemedButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const contextualTheme = useTheme();
    const { children, className, theme = contextualTheme, ...additionalProps } = props;

    const { withBoxShadow, withRoundedCorners } = theme;

    return (
        <RawButton
            {...additionalProps}
            className={classNames(
                withBoxShadow && styles['Variant--shadowed'],
                withRoundedCorners && styles['Variant--rounded'],
                className
            )}
            ref={ref}
        >
            {children}
        </RawButton>
    );
});
